/** Communication log (spec 11, amended by spec 18) — data shapes.
 *
 * **One row per contact — inbound and outbound.** That one sentence is the spine,
 * and everything else follows from it.
 *
 * With Reports dropped from V1 (P352) this is **the only record surface in the
 * product**. It was not shaped for that: inbound was already leaking into a page
 * whose promise was *one row per message every agent has sent*, producing rows
 * like *Carlos Reyes · `Inbound — same-day emergency` · `Sent`* — **nobody sent
 * that; the patient rang.**
 *
 * Nothing is sent from this page. Columns: channel · patient · agent · purpose ·
 * verdict/disposition · outcome · time; the reason is a full-width second line;
 * the drawer always carries the body.
 */

import type { LanguageCode } from '../platform-languages';

export type ClChannel = 'text' | 'email' | 'call';

/** The spine. A contact either came in or went out, and the distinction decides
 * what almost every other column means. */
export type ClDirection = 'inbound' | 'outbound';

export type ClAgent =
  | 'receptionist'
  | 'sms'
  | 'email'
  | 'reminders'
  | 'recall'
  | 'gap-filler'
  | 'treatment'
  | 'invisalign'
  | 'ar';

/**
 * All six verdicts (data contracts §1.2). amber = a rule stopped this for now
 * (held / suppressed); red = somebody must act (blocked / failed); neutral =
 * sent or skipped.
 */
export type ClVerdict = 'sent' | 'held' | 'skipped' | 'blocked' | 'suppressed' | 'failed';

/**
 * The seven inbound dispositions — **the Dashboard's seven, one for one, in the
 * same order and the same colours** (spec 18 §3.2).
 *
 * That correspondence is the whole point: every disposition row on the Dashboard
 * links into this page, and the chip the user lands on is the row they clicked.
 * The strings are therefore a **shared contract** across the two surfaces, not two
 * lists that happen to match today.
 *
 * **They are the complete set. There is no `Other` and there must never be one** —
 * an unmapped disposition is a bug to surface, not a bucket to invent.
 */
export type ClDisposition =
  | 'booked'
  | 'moved'
  | 'answered'
  | 'message'
  | 'warm_transfer'
  | 'urgent_triage'
  | 'cancelled';

export const CL_DISPOSITIONS: { id: ClDisposition; label: string; colour: string }[] = [
  { id: 'booked', label: 'Booked', colour: '#4C5BD4' },
  { id: 'moved', label: 'Moved', colour: '#7B8AE6' },
  { id: 'answered', label: 'Answered, nothing else needed', colour: '#2E9E6B' },
  { id: 'message', label: 'Took a message', colour: '#98A0B2' },
  { id: 'warm_transfer', label: 'Warm transfer', colour: '#D9A22B' },
  { id: 'urgent_triage', label: 'Urgent triage', colour: '#BE4440' },
  { id: 'cancelled', label: 'Cancelled', colour: '#C3C9D6' },
];

/**
 * The appointment-reason slug, **shared with the Dashboard's reason grouping** so
 * both surfaces count the same thing (spec 19 §7.4). A reason bar on the Dashboard
 * links here with `reason=<this>`.
 */
export function reasonSlug(reason: string): string {
  return reason
    .toLowerCase()
    .replace(/[—–]/g, ' ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Which day bucket the row falls in — drives the table's date separators (§7.2). */
export type ClDay = 'today' | 'yesterday' | (string & {});

export interface ClTranscriptLine {
  who: string;
  text: string;
  at: string; // "0:04"
  isAgent?: boolean;
}

export interface ClRow {
  id: string;
  /** Outbound unless stated — the overwhelming majority of rows, and the shape the
   * page was originally built for. */
  direction?: ClDirection;
  /**
   * Nullable **and that is normal** (spec 18 §11.4). An inbound call from a number
   * that resolves to no chart is often a new patient, which is the most valuable
   * kind. Do not drop, do not backfill, do not guess.
   */
  patient: string;
  /** Inbound, unmatched: the formatted number, rendered with `Not in the chart`
   * beneath it. **It must never render empty and must never be dropped** — a
   * missing row and a matched row look identical to a reader. */
  callerNumber?: string;
  /** A batch is one decision — the patient cell reads e.g. "14 reactivation patients". */
  batchCount?: number;
  agent: ClAgent; // the crediting agent, or the agent that handled an inbound contact
  /** A call the Receptionist placed for another agent (P1/P2): credited here, placed by the line. */
  onBehalfOf?: ClAgent;
  /** Outbound: what it was for. Inbound: **what the patient called about**, in
   * their terms. */
  purpose: string;
  channel: ClChannel;
  /**
   * The language it actually went in (amendment 25 §5). Absent means English —
   * so the column is quiet on a single-language practice and speaks up where it
   * matters, which is the only place a reader needs it.
   */
  language?: LanguageCode;
  /**
   * Outbound only. **Two vocabularies, never one merged enum** (§11.2) — `Sent`
   * and `Booked` are not alternatives to each other, and a merged enum invites
   * exactly the inbound-row-reading-`Sent` bug this amendment exists to fix.
   */
  verdict: ClVerdict;
  /** Inbound only, and required there. */
  disposition?: ClDisposition;
  /** On a `booked` or `moved` row — the appointment reason, shown in the existing
   * outcome cell rather than a new column (§7). Slugged, it is what the Dashboard's
   * reason bars link by. */
  appointmentReason?: string;
  /** Held/blocked/suppressed: the rule that stopped it — shown on a full-width second line. */
  reason?: string;
  /** The consequence, never a restatement of the verdict ("Queued for 9:00am", "Retries in 3 days"). */
  outcome: string;
  /** Warm-transfer / bounce read red; paid/booked/replied read green; the rest neutral. */
  outcomeTone?: 'good' | 'alert' | 'muted';
  time: string; // relative, e.g. "2 min ago"
  absoluteTime: string; // for the drawer, e.g. "Wed 24 Sep · 9:02am"
  day: ClDay;
  /** The rank this message won at (Brain §28) — shown in the drawer's decision trail. */
  rank?: string;
  /** A consolidated send carries two purposes and both campaigns claim it. */
  shared?: boolean;
  /** Call-only. */
  durationSec?: number;
  hasRecording?: boolean;
  /** Drawer: email subject, the message body (required on every row), the call transcript. */
  subject?: string;
  body?: string;
  transcript?: ClTranscriptLine[];
  /** Batch drawer: the names behind a batch row. */
  batchNames?: string[];
}

export interface CommLogData {
  titleLead: string;
  titleAccent: string;
  subtitle: string;
  /** Total for the day + how many the agents chose not to send — the eyebrow's two facts. */
  dayTotal: number;
  dayChoseNotToSend: number;
  rows: ClRow[];
}

/** The date windows this page offers. It carries the flexibility precisely because
 * the Dashboard deliberately has only two presets (P331) — this is the page you
 * come to when those two are not the question (spec 18 §6). */
export type ClRange = 'today' | '24h' | '7d' | '30d' | 'custom';

export const CL_RANGES: { id: ClRange; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: '24h', label: 'Past 24 hours' },
  { id: '7d', label: 'Past 7 days' },
  { id: '30d', label: 'Past 30 days' },
  { id: 'custom', label: 'Custom' },
];

/**
 * The three empty cases, borrowed wholesale from `DashStates` §10 rather than
 * re-invented (spec 18 §9).
 *
 * **A filter that matches nothing and a query that could not run look identical
 * unless we make them different** — and they lead a reader to opposite conclusions
 * about the product.
 */
export type ClEmptyState =
  | { state: 'zero' }
  | { state: 'not_set_up' }
  | { state: 'unknown'; lastGoodRead: string };
